/* Baby Fund automatic monthly deduction (on payday) control. */
import { useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { CalendarClock, Loader2, Power } from "lucide-react";

export default function BabyAutoControl({ fund, onChanged, onError }) {
  const auto = fund.auto || {};
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(auto.amount || 50);
  const [busy, setBusy] = useState(false);

  const save = async (enabled, amt) => {
    setBusy(true);
    try {
      await api.patch(`/baby-funds/${fund.id}/auto`, { enabled, amount: Number(amt) || 0 });
      setEditing(false); onChanged && onChanged();
    } catch (e) { onError && onError(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-lg border border-sand bg-cream p-3 text-xs" data-testid={`baby-auto-${fund.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1.5 text-moss">
          <CalendarClock className="w-3.5 h-3.5 text-forest" />
          {auto.enabled
            ? <span data-testid={`baby-auto-summary-${fund.id}`}><strong className="text-ink">{fmtMoney(auto.amount)}</strong> monthly on payday · next {auto.next_due}{auto.paid_this_period && <span className="text-forest"> · paid this month</span>}</span>
            : <span data-testid={`baby-auto-summary-${fund.id}`}>No automatic deduction yet</span>}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setEditing((v) => !v)} className="text-forest hover:underline font-medium" data-testid={`baby-auto-edit-${fund.id}`}>
            {auto.enabled ? "Change" : "Set up"}
          </button>
          {auto.enabled && (
            <button type="button" onClick={() => save(false, auto.amount)} disabled={busy} className="inline-flex items-center gap-1 text-sage hover:text-clay" data-testid={`baby-auto-off-${fund.id}`}>
              <Power className="w-3 h-3" /> Turn off
            </button>
          )}
        </div>
      </div>
      {editing && (
        <form onSubmit={(e) => { e.preventDefault(); save(true, amount); }} className="mt-2 flex items-center gap-2" data-testid={`baby-auto-form-${fund.id}`}>
          <input type="number" min="5" max="2000" step="5" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-28 bg-white border border-sand rounded-md px-2 py-1.5 font-mono" data-testid={`baby-auto-amount-${fund.id}`} />
          <span className="text-sage">per month, pulled from Spending on payday</span>
          <button type="submit" disabled={busy} className="bg-forest text-white rounded-md px-3 py-1.5 font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center gap-1" data-testid={`baby-auto-save-${fund.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save
          </button>
        </form>
      )}
    </div>
  );
}
